/**
 * SmartRecommendations - AI-driven action suggestions
 *
 * Shows intelligent recommendations based on:
 * - Current state of categories
 * - Data quality scores
 * - Missing content gaps
 * - High-opportunity areas
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Lightbulb,
  ArrowRight,
  Sparkles,
  AlertTriangle,
  TrendingUp,
  FileText,
  Target,
  Clock,
  Zap,
  CheckCircle2
} from 'lucide-react';

export interface Recommendation {
  id: string;
  type: 'opportunity' | 'quality' | 'content' | 'optimization' | 'quick_win';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  reason: string;
  action: {
    label: string;
    path: string;
  };
  metrics?: {
    impact?: string;
    effort?: string;
    searchVolume?: number;
  };
}

interface SmartRecommendationsProps {
  recommendations: Recommendation[];
  loading?: boolean;
  maxDisplay?: number;
}

const typeConfig: Record<Recommendation['type'], {
  icon: React.ElementType;
  color: string;
  bgColor: string;
  label: string;
}> = {
  opportunity: {
    icon: TrendingUp,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-950/50',
    label: 'Opportunity'
  },
  quality: {
    icon: AlertTriangle,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-950/50',
    label: 'Data Quality'
  },
  content: {
    icon: FileText,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-950/50',
    label: 'Content Gap'
  },
  optimization: {
    icon: Target,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-950/50',
    label: 'Optimization'
  },
  quick_win: {
    icon: Zap,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-950/50',
    label: 'Quick Win'
  }
};

const priorityConfig: Record<Recommendation['priority'], {
  border: string;
  badge: string;
}> = {
  high: {
    border: 'border-l-4 border-l-red-500',
    badge: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400'
  },
  medium: {
    border: 'border-l-4 border-l-amber-500',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400'
  },
  low: {
    border: 'border-l-4 border-l-blue-500',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400'
  }
};

export function SmartRecommendations({ recommendations, loading, maxDisplay = 4 }: SmartRecommendationsProps) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-purple-500/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Recommended Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-6">
          <div className="animate-pulse flex items-center gap-2 text-muted-foreground">
            <Sparkles className="h-4 w-4" />
            Analyzing your workspace...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!recommendations || recommendations.length === 0) {
    return (
      <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/30">
        <CardContent className="flex items-center gap-4 py-6">
          <div className="p-3 bg-green-100 dark:bg-green-950/50 rounded-full">
            <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h4 className="font-semibold text-green-900 dark:text-green-100">All caught up!</h4>
            <p className="text-sm text-green-700 dark:text-green-400">
              No urgent recommendations right now. Keep up the great work!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort by priority
  const sortedRecommendations = [...recommendations]
    .sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    })
    .slice(0, showAll ? recommendations.length : maxDisplay);

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-purple-500/5">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-amber-500" />
          Recommended Actions
          <Badge variant="secondary" className="ml-2">
            {recommendations.length}
          </Badge>
        </CardTitle>
        <CardDescription>
          Smart suggestions based on your current progress and opportunities
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {sortedRecommendations.map((rec) => {
          const config = typeConfig[rec.type];
          const priority = priorityConfig[rec.priority];
          const TypeIcon = config.icon;

          return (
            <div
              key={rec.id}
              className={`
                p-4 rounded-lg bg-background/80 backdrop-blur-sm
                ${priority.border}
                hover:shadow-md transition-shadow
              `}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`p-2 rounded-lg ${config.bgColor}`}>
                    <TypeIcon className={`h-4 w-4 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className={`${config.bgColor} ${config.color} border-0 text-xs`}>
                        {config.label}
                      </Badge>
                      <Badge variant="outline" className={`${priority.badge} border-0 text-xs`}>
                        {rec.priority.charAt(0).toUpperCase() + rec.priority.slice(1)} Priority
                      </Badge>
                      {rec.metrics?.impact && (
                        <Badge variant="outline" className="text-xs">
                          {rec.metrics.impact} impact
                        </Badge>
                      )}
                    </div>
                    <h4 className="font-semibold text-sm">{rec.title}</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {rec.description}
                    </p>
                    <p className="text-xs text-primary/80 mt-1.5 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      {rec.reason}
                    </p>
                    {rec.metrics && (
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        {rec.metrics.searchVolume && (
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {rec.metrics.searchVolume.toLocaleString()}/mo searches
                          </span>
                        )}
                        {rec.metrics.effort && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {rec.metrics.effort}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate(rec.action.path)}
                  className="flex-shrink-0"
                >
                  {rec.action.label}
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>
          );
        })}

        {recommendations.length > maxDisplay && (
          <div className="text-center pt-2">
            <Button variant="ghost" size="sm" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show less' : `View all ${recommendations.length} recommendations`}
              <ArrowRight className={`h-3 w-3 ml-1 transition-transform ${showAll ? 'rotate-90' : ''}`} />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SmartRecommendations;
